import { LinearProgressWithLabel } from '@/components/cliv-materialUi/LinearProgressWithLabel';
import { useAppSelector } from '@/store/hooks';
import { Box } from '@mui/material';
import styles from './SyncProgress.module.scss';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, MouseEvent } from 'react';
import { ReactComponent as DefaultIcon } from '@/assets/icons/default.svg';
import { ReactComponent as InProcessIcon } from '@/assets/icons/inprocess.svg';
import { ReactComponent as FailedIcon } from '@/assets/icons/failed.svg';
import { ReactComponent as SuccessIcon } from '@/assets/icons/success.svg';
import { clearProgress, resetUploadProgress } from '@/store/slices/uploadProgressSlice';
import { SyncStatus } from '@/enums/SyncStatus.enum';
import { startUpload } from '@/serviceworker/mediator';
import store from '@/store/store';
import { savedInspectionsStorageV2 } from '@/store/idbStorage/savedInspectionsStorageV2';
import { useNetwork } from '@/hooks/useNetwork';
import { tryAutoReloadForMissingServiceWorkerController } from '@/utils/serviceWorkerAutoReload.util';

export const SyncProgress = () => {
  const updates = useAppSelector((state) => state.uploadProgress);
  const { t: translate } = useTranslation();
  const [showUpload, setShowUpload] = useState(false);
  const [hasSavedInspections, setHasSavedInspections] = useState<boolean | null>(null);
  const isOnline = useNetwork();

  useEffect(() => {
    const checkInspections = async () => {
      const hasInspections = await savedInspectionsStorageV2.hasInspections();
      setHasSavedInspections(hasInspections);
      const hasStaleUploadState =
        !hasInspections &&
        ((updates.toUpload?.length ?? 0) > 0 || (updates.failed?.length ?? 0) > 0);
      if (hasStaleUploadState) {
        store.dispatch(resetUploadProgress());
        setShowUpload(false);
        return;
      }
      setShowUpload((updates.toUpload?.length ?? 0) > 0 || hasInspections);
    };
    checkInspections();
  }, [updates.toUpload?.length, updates.failed?.length]);

  useEffect(() => {
    if (!showUpload) {
      return;
    }
    tryAutoReloadForMissingServiceWorkerController();
  }, [showUpload]);

  const getCompletedInspectionsCount = (): number => {
    return updates.status === SyncStatus.IN_PROGRESS
      ? updates.completed?.length + 1
      : updates.completed?.length || 0;
  };
  const isFailedToUpload = updates.failed?.length > 0;
  const isCompleted =
    updates.status === SyncStatus.COMPLETED &&
    getCompletedInspectionsCount() > 0 &&
    !isFailedToUpload;
  const isUploading = updates.status === SyncStatus.IN_PROGRESS;
  const getMessage = (): string => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      switch (true) {
        case isUploading:
          return updates.message;
        case isFailedToUpload:
          return updates.message;
        case isCompleted:
          return translate('upload_completed_successfully');
        case showUpload && !isOnline:
          return translate('offline_data_available_for_upload_no_internet');
        case showUpload:
          return translate('offline_data_available_for_upload');
        default:
          return updates.message;
      }
    } else {
      return translate('Refresh_Page_To_Upload_Data');
    }
  };
  function handlUploadClick(_: MouseEvent<HTMLButtonElement>): void {
    startUpload([]);
  }

  const handleRefreshClick = (_: MouseEvent<HTMLButtonElement>): void => {
    window.location.reload();
  };

  const handleCloseClick = (_: MouseEvent<HTMLButtonElement>): void => {
    store.dispatch(clearProgress());
  };

  const isOrphanedUploadFailure = isFailedToUpload && hasSavedInspections === false;

  const getProgressBarColor = (): string => {
    if (isFailedToUpload) return '#FF5050'; // Upload failed
    if (isCompleted) return '#4DAA57'; // Upload completed successfully
    if (updates.status === SyncStatus.IN_PROGRESS) return '#1976D2'; // Uploading
    return '#FFB800'; // Uploading
  };

  const getIcon = () => {
    if (isFailedToUpload) return <FailedIcon />;
    if (isCompleted) return <SuccessIcon />;
    if (isUploading) return <InProcessIcon />;
    return <DefaultIcon />;
  };
  const getActionButton = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      switch (true) {
        case isOrphanedUploadFailure:
          return (
            <button className={styles.closeButton} onClick={handleCloseClick}>
              Close
            </button>
          );
        case isFailedToUpload && isOnline:
          return (
            <button className={styles.retryButton} onClick={handlUploadClick}>
              Retry
            </button>
          );
        case isCompleted:
          return (
            <button className={styles.closeButton} onClick={handleCloseClick}>
              Close
            </button>
          );
        case showUpload && isOnline && updates.status !== SyncStatus.IN_PROGRESS:
          return (
            <button className={styles.uploadButton} onClick={handlUploadClick}>
              Upload
            </button>
          );
        default:
          return <></>;
      }
    } else {
      return (
        <button className={styles.retryButton} onClick={handleRefreshClick}>
          Refresh
        </button>
      );
    }
  };

  return showUpload ? (
    <section className={styles.progressContainer}>
      <div className={styles.messageContainer}>
        <div className={styles.messageInfo}>
          <div className={styles.icon}>{getIcon()}</div>
          <span>{getMessage()}</span>
        </div>
        {'serviceWorker' in navigator &&
        navigator.serviceWorker.controller &&
        updates.status !== SyncStatus.IDLE ? (
          <span>{`${getCompletedInspectionsCount()}/${getTotalInspectionCount() || 0} inspection(s)`}</span>
        ) : (
          <></>
        )}
      </div>
      <div className={styles.progressbar}>
        {
          <Box sx={{ width: '80%' }}>
            <LinearProgressWithLabel
              showPercentage={isFailedToUpload || isCompleted || isUploading}
              value={updates.progress || 0}
              barColor={getProgressBarColor()}
            />
          </Box>
        }
        {getActionButton()}
      </div>
    </section>
  ) : (
    <></>
  );

  function getTotalInspectionCount() {
    return updates.toUpload?.length || 0;
  }
};
